import { createContext, useContext, useEffect, useId } from "react";

export type FormFieldContextValue = {
  labelId: string;
  hasLabel: boolean;
  hasExternalLabel: boolean;
  required: boolean;
  controlId: string | undefined;
  registerLabel: (id?: string) => () => void;
  registerControl: (id: string) => () => void;
};

export const FormFieldContext = createContext<FormFieldContextValue | null>(
  null,
);

export const useFormFieldContext = () => useContext(FormFieldContext);

type FormControlOptions = {
  id?: string;
  register?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

// The field's label names the first control rendered inside it, so fields
// that hold several controls never end up with duplicate ids or names.
export const useFormControl = ({
  id,
  register: shouldRegister = true,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: FormControlOptions): {
  id: string | undefined;
  "aria-labelledby": string | undefined;
  "aria-required"?: true;
} => {
  const field = useFormFieldContext();
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const register = shouldRegister ? field?.registerControl : undefined;

  useEffect(() => register?.(controlId), [register, controlId]);

  if (!field || !shouldRegister) {
    return { id, "aria-labelledby": ariaLabelledBy };
  }

  const isNamedByField =
    field.hasLabel &&
    field.controlId === controlId &&
    !ariaLabel &&
    !ariaLabelledBy;

  return {
    id: controlId,
    "aria-labelledby": isNamedByField ? field.labelId : ariaLabelledBy,
    ...(field.required && field.controlId === controlId
      ? { "aria-required": true as const }
      : null),
  };
};
